/**
 * useInvoicesPage — data-fetching, filtering, and pagination state for the
 * Invoices / Bills pages.
 */

import { useState, useCallback, useEffect, useRef } from 'react'

import { useParams, useNavigate, useLocation } from 'react-router-dom'

import { InvoiceStatus } from '@/gen/proto'
import type { Party } from '@/gen/proto'

import { VENDOR_TYPES, type ViewMode } from './invoices-helpers'
import { useToast, type Toast } from '../../components/ui'
import {
  listInvoices,
  getInvoice,
  listParties as listPartiesApi,
} from '../../services'
import { useLedgerContext } from '../../stores/ledger-store'
import { useOrganization } from '../../stores/organization-store'

import type { Invoice } from '../../types/billing'
import type { FilterBarCondition } from '@elsapiens/ui'

const PAGE_SIZE = 25

// ── Return type ───────────────────────────────────────────────────────────────

export interface InvoicesPageState {
  // Routing helpers
  isVendor: boolean
  isProforma: boolean
  basePath: string
  statusFilter: InvoiceStatus

  // View state
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  selectedInvoice: Invoice | null
  setSelectedInvoice: (inv: Invoice | null) => void
  /** Seed data when creating a clone (carried via router navigation state). */
  cloneSource: Partial<Invoice> | null
  isLoading: boolean
  setIsLoading: (v: boolean) => void

  // Data
  invoices: Invoice[]
  setInvoices: (inv: Invoice[]) => void
  parties: Party[]

  // Pagination (page-based)
  page: number
  setPage: (p: number) => void
  totalCount: number
  totalPages: number
  hasNextPage: boolean
  isLoadingMore: boolean

  // Server-side filter/sort/search state
  searchQuery: string
  setSearchQuery: (q: string) => void
  sortField: string
  setSortField: (f: string) => void
  sortDirection: 'asc' | 'desc'
  setSortDirection: (d: 'asc' | 'desc') => void
  activeTab: string
  setActiveTab: (t: string) => void
  conditions: FilterBarCondition[]
  setConditions: (c: FilterBarCondition[]) => void
  hasFilters: boolean

  // Server totals
  sumTotalAmount: number
  sumTaxAmount: number
  currencyOptions: { value: string; label: string }[]

  // Actions
  fetchInvoices: () => Promise<void>
  fetchParties: () => Promise<void>

  // Navigation helpers
  navigate: ReturnType<typeof useNavigate>
  organizationId: string
  currentLedgerId: string | null
}

// ── Routing ───────────────────────────────────────────────────────────────────

interface RoutingFlags {
  isVendor: boolean
  isProforma: boolean
  basePath: string
  statusFilter: InvoiceStatus
}

function useRoutingFlags(): RoutingFlags {
  const location = useLocation()
  const isVendor = location.pathname.startsWith('/bills')
  const isProforma = isVendor
    ? location.pathname.includes('/bills/draft')
    : location.pathname.includes('/proforma')
  const basePath = deriveBasePath(isVendor, isProforma)
  const statusFilter: InvoiceStatus = isProforma
    ? InvoiceStatus.INVOICE_STATUS_DRAFT
    : InvoiceStatus.INVOICE_STATUS_POSTED
  return { isVendor, isProforma, basePath, statusFilter }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

interface UseUrlNavInput {
  id: string | undefined
  organizationId: string
  basePath: string
  setIsLoading: (v: boolean) => void
  setSelectedInvoice: (inv: Invoice | null) => void
  setViewMode: (mode: ViewMode) => void
}

function useUrlNavigation(input: UseUrlNavInput): void {
  const { addToast } = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const { id, organizationId, basePath, setIsLoading, setSelectedInvoice, setViewMode } = input
  const currentPath = location.pathname
  useEffect(() => {
    if (currentPath === '/invoices/new' || currentPath.includes('/new')) {
      setViewMode('create')
      setSelectedInvoice(null)
      return
    }
    if (id) {
      void loadInvoiceFromUrl(id, currentPath, organizationId, addToast, navigate, basePath, setIsLoading, setSelectedInvoice, setViewMode)
    } else {
      setViewMode('list')
      setSelectedInvoice(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, organizationId, basePath, currentPath])
}

export function useInvoicesPage(): InvoicesPageState {
  const { organizationId } = useOrganization()
  const { currentLedgerId } = useLedgerContext()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const cloneSource = (location.state as { cloneSource?: Partial<Invoice> } | null)?.cloneSource ?? null
  const { isVendor, isProforma, basePath, statusFilter } = useRoutingFlags()
  const { addToast } = useToast()

  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [parties, setParties] = useState<Party[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [sumTotalAmount, setSumTotalAmount] = useState(0)
  const [sumTaxAmount, setSumTaxAmount] = useState(0)
  const [currencyOptions, setCurrencyOptions] = useState<{ value: string; label: string }[]>([])

  // Filter state (drives server-side fetching)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState('invoiceDate')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [activeTab, setActiveTab] = useState('all')
  const [conditions, setConditions] = useState<FilterBarCondition[]>([])

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const hasNextPage = page < totalPages
  const hasFilters = Boolean(searchQuery) || conditions.filter(c => !c.id.startsWith('tab-')).length > 0

  // Derive server filter params from conditions
  const paymentStatusCondition = conditions.find(c => c.fieldId === 'paymentStatus')
  const currencyCondition = conditions.find(c => c.fieldId === 'currency')
  const invoiceTypeCondition = conditions.find(c => c.fieldId === 'invoiceType')
  const startDateCondition = conditions.find(c => c.fieldId === 'invoiceDate' && c.operator === 'greater_than')
  const endDateCondition = conditions.find(c => c.fieldId === 'invoiceDate' && c.operator === 'less_than')

  // Track filter fingerprint to reset page when filters change
  const filterRef = useRef({ searchQuery, sortField, sortDirection, conditions, statusFilter, currentLedgerId, isVendor })
  const filtersChanged =
    filterRef.current.searchQuery !== searchQuery ||
    filterRef.current.sortField !== sortField ||
    filterRef.current.sortDirection !== sortDirection ||
    filterRef.current.conditions !== conditions ||
    filterRef.current.statusFilter !== statusFilter ||
    filterRef.current.currentLedgerId !== currentLedgerId ||
    filterRef.current.isVendor !== isVendor

  const fetchInvoices = useCallback(
    async (targetPage?: number) => {
      if (!currentLedgerId) { return }
      const pg = targetPage ?? page
      if (pg === 1) { setIsLoading(true) } else { setIsLoadingMore(true) }
      try {
        const response = await listInvoices(
          organizationId,
          currentLedgerId,
          {
            status: statusFilter,
            search: searchQuery || undefined,
            sortField: sortField !== 'invoiceDate' ? sortField : undefined,
            sortDir: sortDirection,
            paymentStatus: paymentStatusCondition?.value as string | undefined,
            currency: currencyCondition?.value as string | undefined,
            invoiceType: invoiceTypeCondition?.value as import('../../types/billing').InvoiceType | undefined,
            startDate: startDateCondition?.value as string | undefined,
            endDate: endDateCondition?.value as string | undefined,
          },
          { pageSize: PAGE_SIZE, page: pg },
        )
        if (response.data) {
          const allInvoices = (response.data.invoices ?? []).filter(inv => {
            const type = inv.invoiceType?.toLowerCase().replace('invoice_type_', '')
            return isVendor ? VENDOR_TYPES.includes(type as typeof VENDOR_TYPES[number]) : !VENDOR_TYPES.includes(type as typeof VENDOR_TYPES[number])
          })
          setInvoices(allInvoices)
          setTotalCount(response.data.totalCount ?? 0)
          setSumTotalAmount(response.data.sumTotalAmount ?? 0)
          setSumTaxAmount(response.data.sumTaxAmount ?? 0)
          // Build currency options from loaded invoices
          const currencies = [...new Set(allInvoices.map(inv => inv.currency).filter(Boolean))]
          setCurrencyOptions(currencies.map(c => ({ value: c, label: c })))
        } else if (response.error) {
          setInvoices([])
          addToast({ variant: 'error', message: 'Failed to load invoices' })
        }
      } catch {
        setInvoices([])
        addToast({ variant: 'error', message: 'Failed to load invoices' })
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizationId, currentLedgerId, statusFilter, isVendor, page, searchQuery, sortField, sortDirection, paymentStatusCondition?.value, currencyCondition?.value, invoiceTypeCondition?.value, startDateCondition?.value, endDateCondition?.value],
  )

  // Reset page to 1 when filters change
  useEffect(() => {
    if (filtersChanged) {
      filterRef.current = { searchQuery, sortField, sortDirection, conditions, statusFilter, currentLedgerId, isVendor }
      setPage(1)
      setInvoices([])
      setTotalCount(0)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, sortField, sortDirection, conditions, statusFilter, currentLedgerId, isVendor])

  // Fetch on first entry and whenever fetchInvoices' own inputs change (page,
  // filters, ledger, org, vendor/status — see its deps above). Deliberately
  // NOT keyed on `viewMode`: returning to 'list' from a detail/edit/create
  // view leaves the already-loaded rows and paging state intact, so a plain
  // back-navigation must not trigger a redundant refetch.
  useEffect(() => {
    if (viewMode === 'list') { void fetchInvoices(page) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchInvoices, page])

  const fetchParties = useCallback(async () => {
    if (!currentLedgerId) { return }
    try {
      const response = await listPartiesApi({
        orgId: organizationId,
        ledgerId: currentLedgerId,
        filters: { activeOnly: true, role: isVendor ? 'supplier' : 'customer' },
        pageSize: 1000,
      })
      if (response.data?.parties) {
        setParties(response.data.parties)
      } else if (response.error) {
        addToast({ variant: 'error', message: 'Failed to load parties' })
      }
    } catch {
      addToast({ variant: 'error', message: 'Failed to load parties. Please try again.' })
    }
  }, [organizationId, currentLedgerId, addToast, isVendor])

  useEffect(() => {
    void fetchParties()
  }, [fetchParties])

  useUrlNavigation({ id, organizationId, basePath, setIsLoading, setSelectedInvoice, setViewMode })

  return {
    isVendor, isProforma, basePath, statusFilter,
    viewMode, setViewMode,
    selectedInvoice, setSelectedInvoice, cloneSource,
    isLoading, setIsLoading,
    invoices, setInvoices, parties,
    page, setPage, totalCount, totalPages, hasNextPage, isLoadingMore,
    searchQuery, setSearchQuery,
    sortField, setSortField,
    sortDirection, setSortDirection,
    activeTab, setActiveTab,
    conditions, setConditions,
    hasFilters,
    sumTotalAmount, sumTaxAmount, currencyOptions,
    fetchInvoices: () => fetchInvoices(page),
    fetchParties,
    navigate, organizationId, currentLedgerId,
  }
}

// ── Private helpers ───────────────────────────────────────────────────────────

function deriveBasePath(isVendor: boolean, isProforma: boolean): string {
  if (isVendor) { return isProforma ? '/bills/draft' : '/bills/final' }
  return isProforma ? '/invoices/proforma' : '/invoices/final'
}

// eslint-disable-next-line max-params -- TODO(refactor): convert to options object
async function loadInvoiceFromUrl(
  id: string,
  currentPath: string,
  organizationId: string,
  addToast: (t: Omit<Toast, 'id'>) => void,
  navigate: ReturnType<typeof useNavigate>,
  basePath: string,
  setIsLoading: (v: boolean) => void,
  setSelectedInvoice: (inv: Invoice | null) => void,
  setViewMode: (mode: ViewMode) => void,
): Promise<void> {
  setIsLoading(true)
  try {
    const response = await getInvoice(organizationId, id)
    if (response.data) {
      setSelectedInvoice(response.data)
      setViewMode(currentPath.endsWith('/edit') ? 'edit' : 'view')
    } else {
      addToast({ variant: 'error', message: 'Invoice not found' })
      navigate(basePath)
    }
  } catch {
    addToast({ variant: 'error', message: 'Failed to load invoice' })
    navigate(basePath)
  } finally {
    setIsLoading(false)
  }
}
